import { useEffect, useState, type ReactNode } from "react";
import { ArrowLeft, ChevronDown, MoreHorizontal, SlidersHorizontal, X } from "lucide-react";
import { DropdownMenu } from "radix-ui";
import { useGuardedNavigate } from "../lib/guard.tsx";
import { hasInAppHistory } from "../lib/nav.tsx";
import { Button, cx } from "./ui.tsx";

/**
 * "Voltar" (UX-01): returns to the exact previous in-app page (its filters and scroll come back with it);
 * opened from a shared link, it goes to the parent list instead.
 */
export function BackLink({ fallback, label }: { fallback: string; label: string }) {
  const go = useGuardedNavigate();
  return (
    <a
      href={fallback}
      className="elevate tap inline-flex items-center gap-1.5 rounded-ctl px-2 text-sm font-semibold text-primary"
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        if (hasInAppHistory()) window.history.back();
        else go(fallback);
      }}
    >
      <ArrowLeft className="size-4" aria-hidden />
      {label}
    </a>
  );
}

/**
 * Filters: always visible on desktop; behind a "Filtros (n)" toggle on phones with chips for active filters
 * (UX-10). `chips` are the active filters with a remove action.
 */
export function FilterPanel({ children, chips, onClear }: {
  children: ReactNode;
  chips: { label: string; onRemove: () => void }[];
  onClear?: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-4">
      <div className="flex flex-wrap items-center gap-2 md:hidden">
        <Button variant="secondary" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          <SlidersHorizontal className="size-4" aria-hidden />
          Filtros{chips.length ? ` (${chips.length})` : ""}
          <ChevronDown className={cx("size-4 transition-transform", open && "rotate-180")} aria-hidden />
        </Button>
      </div>
      <div className={cx(open ? "block" : "hidden", "md:block")}>{children}</div>
      {chips.length > 0 && (
        <div className="mt-1 flex flex-wrap items-center gap-2" aria-label="Filtros ativos">
          {chips.map((c) => (
            <button key={c.label} type="button" onClick={c.onRemove}
              className="inline-flex min-h-8 items-center gap-1 rounded-full border border-soft-ink/25 bg-soft px-3 text-xs font-semibold text-soft-ink transition-colors duration-[var(--duration-base)] hover:border-soft-ink/60 hover:bg-surface">
              {c.label}
              <X className="size-3.5" aria-label="Remover filtro" />
            </button>
          ))}
          {onClear && chips.length > 1 && (
            <button type="button" onClick={onClear} className="min-h-8 rounded-full px-2 text-xs font-semibold text-muted hover:bg-line/60 hover:text-ink">
              Limpar filtros
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Sticky bottom action bar for long editors (UX-14): the unsaved indicator and the main commands stay in reach.
 * Ctrl/Cmd+S triggers `onSave` when provided.
 */
export function StickyActions({ dirty, children, summary, onSave }: { dirty: boolean; children: ReactNode; summary?: ReactNode; onSave?: () => void }) {
  useEffect(() => {
    if (!onSave) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        onSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onSave]);
  return (
    <div className="sticky bottom-0 z-20 -mx-4 mt-6 border-t border-line bg-surface/90 px-4 py-3 shadow-[0_-10px_24px_-16px_rgb(13_40_69/0.28)] backdrop-blur-md backdrop-saturate-150 sm:-mx-7 sm:px-7">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="mr-auto flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          {dirty ? (
            <span className="inline-flex items-center gap-1.5 font-semibold text-warn-ink">
              <span className="size-2 rounded-full bg-[#d9932a]" aria-hidden />
              Alterações não salvas
            </span>
          ) : (
            <span className="text-muted">Tudo salvo</span>
          )}
          {summary}
        </div>
        <div className="flex flex-wrap items-center gap-2">{children}</div>
      </div>
    </div>
  );
}

export interface MenuItem {
  label: string;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
}

/** Row overflow menu "⋯" (keyboard accessible via Radix). */
export function OverflowMenu({ label, items }: { label: string; items: (MenuItem | "separator")[] }) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button type="button" aria-label={label}
          className="elevate inline-flex size-9 shrink-0 items-center justify-center rounded-ctl text-muted hover:text-ink data-[state=open]:bg-paper data-[state=open]:text-ink">
          <MoreHorizontal className="size-5" aria-hidden />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={4} className="z-50 min-w-48 rounded-panel border border-line bg-surface p-1.5 shadow-e3 animate-scale-in">
          {items.map((it, i) =>
            it === "separator" ? (
              <DropdownMenu.Separator key={i} className="my-1 h-px bg-line" />
            ) : (
              <DropdownMenu.Item key={it.label} disabled={it.disabled} onSelect={it.onSelect}
                className={cx(
                  "flex min-h-9 items-center gap-2 rounded-[8px] px-3 text-sm outline-none select-none transition-colors duration-[var(--duration-base)]",
                  "data-[highlighted]:bg-paper data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40",
                  it.danger ? "text-danger-ink data-[highlighted]:bg-danger" : "text-ink",
                )}>
                {it.icon}
                {it.label}
              </DropdownMenu.Item>
            ),
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
